import classNames from 'classnames';
import { when } from 'mobx';
import { observer } from 'mobx-react-lite';
import React from 'react';
import type { ActionMeta, ValueType } from 'react-select';

import type {
  IMailrailPartnerMessage,
  IPartner,
  IPinpointPartnerMessage,
  MailrailPartnerMessage,
  PinpointPartnerMessage,
} from '@feathr/blackbox';
import { CampaignClass } from '@feathr/blackbox';
import { AsyncSelect } from '@feathr/components';
import { useStore } from '@feathr/extender/state';

import * as styles from './MessageSelect.css';

type TMessage = PinpointPartnerMessage | MailrailPartnerMessage;
type TMessageAttributes = IPinpointPartnerMessage | IMailrailPartnerMessage;

interface IMessageSelectProps {
  className?: string;
  eventId: string;
  id?: string;
  onClear: () => void;
  placeholder?: string;
}

export interface IMessageSelectMultiProps extends IMessageSelectProps {
  isMulti: true;
  onChange: (value: string[]) => void;
  value?: string[];
}

export interface IMessageSelectSingleProps extends IMessageSelectProps {
  isMulti?: false;
  onChange: (value: string) => void;
  value?: string;
}

function MessageSelect({
  className,
  eventId,
  id,
  isMulti = false,
  onChange,
  onClear,
  placeholder,
  value,
}: IMessageSelectMultiProps | IMessageSelectSingleProps) {
  const { Campaigns } = useStore();

  async function loadOptions(inputValue: string) {
    const data = Campaigns.list({
      filters: {
        _cls__in: [CampaignClass.PinpointPartnerMessage, CampaignClass.MailrailPartnerMessage],
        name__icontains: inputValue || undefined,
        _parent: eventId,
      },
    });
    await when(() => !data.isPending);
    return data.models
      .filter((model) => {
        if (Array.isArray(value)) {
          return !value.includes(model.id);
        }
        return !value || value !== model.id;
      })
      .map((model) => {
        return {
          ...model.toJS(),
          name: (model as TMessage).getItemLabel(),
        };
      });
  }

  function handleChange(
    newValue: ValueType<TMessageAttributes>,
    action: ActionMeta<TMessageAttributes>,
  ) {
    if (['select-option', 'remove-value', 'clear'].includes(action.action)) {
      if (Array.isArray(newValue)) {
        if (newValue.length) {
          (onChange as IMessageSelectMultiProps['onChange'])(
            (newValue as IPartner[]).map((v) => v.id!),
          );
        } else {
          onClear();
        }
      } else if (newValue) {
        (onChange as IMessageSelectSingleProps['onChange'])((newValue as TMessageAttributes).id!);
      } else {
        onClear();
      }
    }
  }

  return (
    <AsyncSelect
      defaultOptions={true}
      id={id}
      isClearable={true}
      isMulti={isMulti}
      loadOptions={loadOptions}
      name={'message-select'}
      noOptionsMessage={({ inputValue }) =>
        inputValue.length ? 'No results' : 'Start typing the name of a partner message...'
      }
      onChange={handleChange}
      placeholder={placeholder}
      value={
        Array.isArray(value)
          ? value.map((valueId) => {
              const msg = Campaigns.get(valueId) as TMessage;
              return { ...msg.toJS(), name: msg.getItemLabel() };
            })
          : value
            ? {
                ...(Campaigns.get(value) as TMessage).toJS(),
                name: (Campaigns.get(value) as TMessage).getItemLabel(),
              }
            : undefined
      }
      wrapperClassName={classNames(className, styles.root)}
    />
  );
}

export default observer(MessageSelect);
